import { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Calendar, Users, MapPin, Building2, X, FileText, FileSpreadsheet, 
  TrendingUp, ChevronUp, ChevronDown 
} from "lucide-react";
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine 
} from "recharts";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#F97316', '#3B82F6', '#22C55E', '#EAB308', '#8B5CF6', '#EC4899', '#14B8A6', '#F43F5E'];

const formatDateTimeDMY = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

export default function FinancialsTab({ drivers, sites }) {
  // Financials state
  const [siteFinancials, setSiteFinancials] = useState([]);
  const [financialsByDate, setFinancialsByDate] = useState([]);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  
  // Filters state
  const [financialsDateRange, setFinancialsDateRange] = useState({ start: "", end: "" });
  const [financialsFilterDrivers, setFinancialsFilterDrivers] = useState([]);
  const [financialsFilterSites, setFinancialsFilterSites] = useState([]);
  const [financialsFilterCustomers, setFinancialsFilterCustomers] = useState([]);
  const [showFinancialsDriverFilter, setShowFinancialsDriverFilter] = useState(false);
  const [showFinancialsSiteFilter, setShowFinancialsSiteFilter] = useState(false);
  const [showFinancialsCustomerFilter, setShowFinancialsCustomerFilter] = useState(false);
  
  // Sorting state
  const [financialsSortColumn, setFinancialsSortColumn] = useState("profit_loss");
  const [financialsSortDirection, setFinancialsSortDirection] = useState("desc");
  const isLoadingRef = useRef(false);

  // Fetch data on mount and when filters change
  useEffect(() => {
    if (isLoadingRef.current) {
      console.log("[FinancialsTab] Already loading, skipping...");
      return;
    }
    console.log("[FinancialsTab] useEffect triggered, fetching data...");
    fetchFinancials();
  }, [financialsDateRange.start, financialsDateRange.end, financialsFilterDrivers.length, financialsFilterSites.length, financialsFilterCustomers.length]);

  const fetchFinancials = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    console.log("[FinancialsTab] fetchFinancials called");
    setFinancialsLoading(true);
    try {
      const params = new URLSearchParams();
      if (financialsDateRange.start) params.append('start_date', financialsDateRange.start);
      if (financialsDateRange.end) params.append('end_date', financialsDateRange.end);
      if (financialsFilterDrivers.length > 0) params.append('driver_ids', financialsFilterDrivers.join(','));
      if (financialsFilterSites.length > 0) params.append('site_ids', financialsFilterSites.join(','));
      if (financialsFilterCustomers.length > 0) params.append('customers', financialsFilterCustomers.join(','));
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      console.log("[FinancialsTab] Fetching both APIs in parallel...");
      
      // Fetch both APIs in parallel to reduce total wait time
      const [financialsRes, financialsByDateRes] = await Promise.all([
        axios.get(`${API}/analytics/site-financials${queryString}`, { timeout: 90000 }),
        axios.get(`${API}/analytics/financials-by-date?days=30${queryString ? '&' + params.toString() : ''}`, { timeout: 90000 })
      ]);
      
      console.log("[FinancialsTab] site-financials received:", financialsRes.data?.length);
      console.log("[FinancialsTab] financials-by-date received:", financialsByDateRes.data?.length);
      
      setSiteFinancials(financialsRes.data || []);
      setFinancialsByDate(financialsByDateRes.data || []);
      console.log("[FinancialsTab] All done!");
    } catch (error) {
      console.error("[FinancialsTab] Error:", error);
      setSiteFinancials([]);
      setFinancialsByDate([]);
    } finally {
      setFinancialsLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Sorted financials
  const sortedSiteFinancials = useMemo(() => {
    return [...siteFinancials].sort((a, b) => {
      let aVal = a[financialsSortColumn];
      let bVal = b[financialsSortColumn];
      
      if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }
      
      if (financialsSortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }, [siteFinancials, financialsSortColumn, financialsSortDirection]);

  const handleFinancialsSort = (column) => {
    if (financialsSortColumn === column) {
      setFinancialsSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setFinancialsSortColumn(column);
      setFinancialsSortDirection('desc');
    }
  };

  const SortIcon = ({ column }) => {
    if (financialsSortColumn !== column) return <span className="ml-1 text-white/30">↕</span>;
    return <span className="ml-1">{financialsSortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const exportToPDF = () => {
    try {
      if (!sortedSiteFinancials || sortedSiteFinancials.length === 0) {
        toast.error("No financial data to export");
        return;
      }
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Site Financials Report", 14, 22);
      doc.setFontSize(10);
      doc.text(`Generated: ${formatDateTimeDMY(new Date())}`, 14, 30);
      
      autoTable(doc, {
        startY: 40,
        head: [['Site', 'Customer', 'Loads', 'Cost/Load', 'Sales/Load', 'Total Cost', 'Total Sales', 'P/L', 'Margin']],
        body: sortedSiteFinancials.map(s => [
          s.site_name || '',
          s.customer_name || '-',
          s.total_deliveries || 0,
          s.cost_per_load ? `$${s.cost_per_load}` : '-',
          s.sales_per_load ? `$${s.sales_per_load}` : '-',
          s.total_cost ? `$${Number(s.total_cost).toLocaleString()}` : '-',
          s.total_sales ? `$${Number(s.total_sales).toLocaleString()}` : '-',
          s.profit_loss !== undefined ? `$${Number(s.profit_loss).toLocaleString()}` : '-',
          s.profit_margin !== undefined ? `${s.profit_margin}%` : '-',
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22] },
      });
      
      doc.save(`financials_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF: " + error.message);
    }
  };

  const exportToExcel = () => {
    const data = sortedSiteFinancials.map(s => ({
      'Site': s.site_name,
      'Customer': s.customer_name || '',
      'Total Loads': s.total_deliveries,
      'Cost Per Load': s.cost_per_load || '',
      'Sales Per Load': s.sales_per_load || '',
      'Total Cost': s.total_cost || '',
      'Total Sales': s.total_sales || '',
      'Profit/Loss': s.profit_loss || '',
      'Profit Margin (%)': s.profit_margin || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Financials");
    XLSX.writeFile(wb, `financials_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel exported successfully!");
  };

  const uniqueCustomers = [...new Set(sites.map(s => s.customer_name).filter(Boolean))];

  // Calculate totals
  const totalCost = sortedSiteFinancials.reduce((sum, s) => sum + (s.total_cost || 0), 0);
  const totalSales = sortedSiteFinancials.reduce((sum, s) => sum + (s.total_sales || 0), 0);
  const totalProfitLoss = sortedSiteFinancials.reduce((sum, s) => sum + (s.profit_loss || 0), 0);
  const avgMargin = totalSales > 0 ? (totalProfitLoss / totalSales * 100) : 0;

  return (
    <div className="space-y-4" data-testid="financials-tab">
      {/* Filters Section */}
      <div className="bg-[#161920] border border-white/10 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-white/60" strokeWidth={2} />
            <input
              type="date"
              value={financialsDateRange.start}
              onChange={(e) => setFinancialsDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="h-9 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm [color-scheme:dark]"
              data-testid="financials-start-date"
            />
            <span className="text-white/40">to</span>
            <input
              type="date"
              value={financialsDateRange.end}
              onChange={(e) => setFinancialsDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="h-9 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm [color-scheme:dark]"
              data-testid="financials-end-date"
            />
          </div>

          {/* Driver Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFinancialsDriverFilter(!showFinancialsDriverFilter)}
              className={`h-9 px-3 border text-sm flex items-center gap-2 ${
                financialsFilterDrivers.length > 0 
                  ? 'bg-primary/20 border-primary text-primary' 
                  : 'bg-transparent border-white/20 text-white/60 hover:border-white/40'
              }`}
            >
              <Users className="w-4 h-4" strokeWidth={2} />
              Drivers {financialsFilterDrivers.length > 0 && `(${financialsFilterDrivers.length})`}
            </button>
            {showFinancialsDriverFilter && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-[#1a1d24] border border-white/20 z-50 max-h-64 overflow-y-auto">
                <div className="p-2 border-b border-white/10">
                  <button onClick={() => setFinancialsFilterDrivers([])} className="text-xs text-primary hover:underline">Clear all</button>
                </div>
                {drivers.map(driver => (
                  <label key={driver.id} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={financialsFilterDrivers.includes(driver.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFinancialsFilterDrivers([...financialsFilterDrivers, driver.id]);
                        } else {
                          setFinancialsFilterDrivers(financialsFilterDrivers.filter(id => id !== driver.id));
                        }
                      }}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-white">{driver.full_name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Site Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFinancialsSiteFilter(!showFinancialsSiteFilter)}
              className={`h-9 px-3 border text-sm flex items-center gap-2 ${
                financialsFilterSites.length > 0 
                  ? 'bg-primary/20 border-primary text-primary' 
                  : 'bg-transparent border-white/20 text-white/60 hover:border-white/40'
              }`}
            >
              <MapPin className="w-4 h-4" strokeWidth={2} />
              Sites {financialsFilterSites.length > 0 && `(${financialsFilterSites.length})`}
            </button>
            {showFinancialsSiteFilter && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-[#1a1d24] border border-white/20 z-50 max-h-64 overflow-y-auto">
                <div className="p-2 border-b border-white/10">
                  <button onClick={() => setFinancialsFilterSites([])} className="text-xs text-primary hover:underline">Clear all</button>
                </div>
                {sites.map(site => (
                  <label key={site.id} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={financialsFilterSites.includes(site.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFinancialsFilterSites([...financialsFilterSites, site.id]);
                        } else {
                          setFinancialsFilterSites(financialsFilterSites.filter(id => id !== site.id));
                        }
                      }}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-white">{site.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Customer Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFinancialsCustomerFilter(!showFinancialsCustomerFilter)}
              className={`h-9 px-3 border text-sm flex items-center gap-2 ${
                financialsFilterCustomers.length > 0 
                  ? 'bg-primary/20 border-primary text-primary' 
                  : 'bg-transparent border-white/20 text-white/60 hover:border-white/40'
              }`}
            >
              <Building2 className="w-4 h-4" strokeWidth={2} />
              Customers {financialsFilterCustomers.length > 0 && `(${financialsFilterCustomers.length})`}
            </button>
            {showFinancialsCustomerFilter && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-[#1a1d24] border border-white/20 z-50 max-h-64 overflow-y-auto">
                <div className="p-2 border-b border-white/10">
                  <button onClick={() => setFinancialsFilterCustomers([])} className="text-xs text-primary hover:underline">Clear all</button>
                </div>
                {uniqueCustomers.map(customer => (
                  <label key={customer} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={financialsFilterCustomers.includes(customer)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFinancialsFilterCustomers([...financialsFilterCustomers, customer]);
                        } else {
                          setFinancialsFilterCustomers(financialsFilterCustomers.filter(c => c !== customer));
                        }
                      }}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-white">{customer}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Clear Filters */}
          {(financialsDateRange.start || financialsDateRange.end || financialsFilterDrivers.length > 0 || financialsFilterSites.length > 0 || financialsFilterCustomers.length > 0) && (
            <button
              onClick={() => {
                setFinancialsDateRange({ start: "", end: "" });
                setFinancialsFilterDrivers([]);
                setFinancialsFilterSites([]);
                setFinancialsFilterCustomers([]);
              }}
              className="h-9 px-3 text-sm text-red-400 hover:text-red-300 flex items-center gap-1"
            >
              <X className="w-4 h-4" strokeWidth={2} />
              Clear Filters
            </button>
          )}

          {/* Export Buttons */}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={exportToPDF}
              className="h-9 px-4 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-sm flex items-center gap-2"
              data-testid="financials-export-pdf"
            >
              <FileText className="w-4 h-4" strokeWidth={2} />
              PDF
            </button>
            <button
              onClick={exportToExcel}
              className="h-9 px-4 bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30 text-sm flex items-center gap-2"
              data-testid="financials-export-xlsx"
            >
              <FileSpreadsheet className="w-4 h-4" strokeWidth={2} />
              XLSX
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {financialsLoading ? (
        <div className="bg-[#161920] border border-white/10 p-12 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white/60">Loading financials...</p>
        </div>
      ) : siteFinancials.length === 0 ? (
        <div className="bg-[#161920] border border-white/10 p-12 text-center">
          <TrendingUp className="w-12 h-12 text-white/20 mx-auto mb-4" strokeWidth={1} />
          <p className="text-white/60">No financial data found. Make sure sites have cost/sales values set.</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#161920] border border-white/10 p-4">
              <p className="text-xs text-white/60 uppercase mb-1">Total Cost</p>
              <p className="text-2xl font-bold text-red-400 font-mono">
                ${totalCost.toLocaleString()}
              </p>
            </div>
            <div className="bg-[#161920] border border-white/10 p-4">
              <p className="text-xs text-white/60 uppercase mb-1">Total Sales</p>
              <p className="text-2xl font-bold text-green-400 font-mono">
                ${totalSales.toLocaleString()}
              </p>
            </div>
            <div className="bg-[#161920] border border-white/10 p-4">
              <p className="text-xs text-white/60 uppercase mb-1">Net Profit/Loss</p>
              <p className={`text-2xl font-bold font-mono ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                ${totalProfitLoss.toLocaleString()}
              </p>
            </div>
            <div className="bg-[#161920] border border-white/10 p-4">
              <p className="text-xs text-white/60 uppercase mb-1">Avg Margin</p>
              <p className={`text-2xl font-bold font-mono ${avgMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {totalSales > 0 ? `${avgMargin.toFixed(1)}%` : '-'}
              </p>
            </div>
          </div>

          {/* Charts Section */}
          <div className="bg-[#161920] border border-white/10 p-6">
            <h3 className="font-heading font-bold text-lg uppercase text-white mb-6">Financial Trends</h3>
            
            {/* Line Charts */}
            {financialsByDate.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-[#1a1d24] border border-white/5 p-4 lg:col-span-2">
                  <h4 className="text-sm font-heading font-bold text-white/60 uppercase mb-4">Total Cost & Sales Over Time</h4>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={financialsByDate}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" tick={{ fill: '#ccc', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#ccc', fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#000', border: '1px solid #F97316', borderRadius: 4 }}
                          labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                          itemStyle={{ color: '#fff' }}
                          formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name]}
                        />
                        <Legend wrapperStyle={{ color: '#fff' }} />
                        <Line type="monotone" dataKey="total_cost" name="Total Cost" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444', r: 3 }} />
                        <Line type="monotone" dataKey="total_sales" name="Total Sales" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E', r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-[#1a1d24] border border-white/5 p-4 lg:col-span-2">
                  <h4 className="text-sm font-heading font-bold text-white/60 uppercase mb-4">Daily Profit/Loss Trend</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={financialsByDate}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" tick={{ fill: '#ccc', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#ccc', fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#000', border: '1px solid #F97316', borderRadius: 4 }}
                          labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                          itemStyle={{ color: '#fff' }}
                          formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Profit/Loss']}
                        />
                        <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                        <Line type="monotone" dataKey="profit_loss" name="P/L" stroke="#F97316" strokeWidth={2} dot={{ fill: '#F97316', r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Bar/Pie Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#1a1d24] border border-white/5 p-4">
                <h4 className="text-sm font-heading font-bold text-white/60 uppercase mb-4">Cost vs Sales by Site</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sortedSiteFinancials.filter(s => s.total_cost > 0 || s.total_sales > 0).slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" tick={{ fill: '#ccc', fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="site_name" tick={{ fill: '#ccc', fontSize: 10 }} width={100} tickFormatter={(v) => v.length > 12 ? v.slice(0,12) + '...' : v} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#000', border: '1px solid #F97316', borderRadius: 4 }}
                        labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                        itemStyle={{ color: '#fff' }}
                        formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name]}
                      />
                      <Legend wrapperStyle={{ color: '#fff' }} />
                      <Bar dataKey="total_cost" name="Total Cost" fill="#EF4444" />
                      <Bar dataKey="total_sales" name="Total Sales" fill="#22C55E" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1a1d24] border border-white/5 p-4">
                <h4 className="text-sm font-heading font-bold text-white/60 uppercase mb-4">Profit/Loss by Site</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sortedSiteFinancials.filter(s => s.profit_loss !== 0).slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="site_name" tick={{ fill: '#ccc', fontSize: 9 }} angle={-45} textAnchor="end" height={80} tickFormatter={(v) => v.length > 10 ? v.slice(0,10) + '...' : v} />
                      <YAxis tick={{ fill: '#ccc', fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#000', border: '1px solid #F97316', borderRadius: 4 }}
                        labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                        itemStyle={{ color: '#fff' }}
                        formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Profit/Loss']}
                      />
                      <Bar dataKey="profit_loss" name="P/L">
                        {sortedSiteFinancials.filter(s => s.profit_loss !== 0).slice(0, 10).map((entry, index) => (
                          <Cell key={index} fill={entry.profit_loss >= 0 ? '#22C55E' : '#EF4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1a1d24] border border-white/5 p-4">
                <h4 className="text-sm font-heading font-bold text-white/60 uppercase mb-4">Profit Distribution</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sortedSiteFinancials.filter(s => s.profit_loss > 0).slice(0, 8)}
                        dataKey="profit_loss"
                        nameKey="site_name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name.slice(0, 8)}${name.length > 8 ? '..' : ''} ${(percent * 100).toFixed(0)}%`}
                      >
                        {sortedSiteFinancials.filter(s => s.profit_loss > 0).slice(0, 8).map((_, index) => (
                          <Cell key={index} fill={CHART_COLORS[index % 8]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#000', border: '1px solid #F97316', borderRadius: 4 }}
                        labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                        itemStyle={{ color: '#fff' }}
                        formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Profit']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1a1d24] border border-white/5 p-4">
                <h4 className="text-sm font-heading font-bold text-white/60 uppercase mb-4">Profit Margin by Site (%)</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sortedSiteFinancials.filter(s => s.profit_margin !== 0).slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="site_name" tick={{ fill: '#ccc', fontSize: 9 }} angle={-45} textAnchor="end" height={80} tickFormatter={(v) => v.length > 10 ? v.slice(0,10) + '...' : v} />
                      <YAxis tick={{ fill: '#ccc', fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#000', border: '1px solid #F97316', borderRadius: 4 }}
                        labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                        itemStyle={{ color: '#fff' }}
                        formatter={(value) => [`${value}%`, 'Margin']}
                      />
                      <ReferenceLine y={0} stroke="#666" />
                      <Bar dataKey="profit_margin" name="Margin">
                        {sortedSiteFinancials.filter(s => s.profit_margin !== 0).slice(0, 10).map((entry, index) => (
                          <Cell key={index} fill={entry.profit_margin >= 0 ? '#22C55E' : '#EF4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Table */}
          <div className="bg-[#161920] border border-white/10 p-6">
            <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Site Financial Details</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th onClick={() => handleFinancialsSort('site_name')} className="text-left py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Site <SortIcon column="site_name" />
                    </th>
                    <th onClick={() => handleFinancialsSort('customer_name')} className="text-left py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Customer <SortIcon column="customer_name" />
                    </th>
                    <th onClick={() => handleFinancialsSort('total_deliveries')} className="text-right py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Loads <SortIcon column="total_deliveries" />
                    </th>
                    <th onClick={() => handleFinancialsSort('cost_per_load')} className="text-right py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Cost/Load <SortIcon column="cost_per_load" />
                    </th>
                    <th onClick={() => handleFinancialsSort('sales_per_load')} className="text-right py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Sales/Load <SortIcon column="sales_per_load" />
                    </th>
                    <th onClick={() => handleFinancialsSort('total_cost')} className="text-right py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Total Cost <SortIcon column="total_cost" />
                    </th>
                    <th onClick={() => handleFinancialsSort('total_sales')} className="text-right py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Total Sales <SortIcon column="total_sales" />
                    </th>
                    <th onClick={() => handleFinancialsSort('profit_loss')} className="text-right py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      P/L <SortIcon column="profit_loss" />
                    </th>
                    <th onClick={() => handleFinancialsSort('profit_margin')} className="text-right py-2 text-white/60 font-semibold uppercase text-xs cursor-pointer hover:text-white select-none">
                      Margin <SortIcon column="profit_margin" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSiteFinancials.map((s, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 text-white">{s.site_name}</td>
                      <td className="py-2 text-white/60">{s.customer_name}</td>
                      <td className="py-2 text-right text-white font-mono">{s.total_deliveries}</td>
                      <td className="py-2 text-right text-white/60 font-mono">{s.cost_per_load ? `$${Number(s.cost_per_load).toLocaleString()}` : '-'}</td>
                      <td className="py-2 text-right text-white/60 font-mono">{s.sales_per_load ? `$${Number(s.sales_per_load).toLocaleString()}` : '-'}</td>
                      <td className="py-2 text-right text-red-400 font-mono">{s.total_cost ? `$${Number(s.total_cost).toLocaleString()}` : '-'}</td>
                      <td className="py-2 text-right text-green-400 font-mono">{s.total_sales ? `$${Number(s.total_sales).toLocaleString()}` : '-'}</td>
                      <td className={`py-2 text-right font-mono font-bold ${s.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(s.total_cost || s.total_sales) ? `$${Number(s.profit_loss).toLocaleString()}` : '-'}
                      </td>
                      <td className={`py-2 text-right font-mono font-bold ${s.profit_margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(s.total_cost || s.total_sales) ? `${s.profit_margin}%` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/20">
                    <td colSpan="5" className="py-3 text-white font-bold uppercase">Total</td>
                    <td className="py-3 text-right text-red-400 font-mono font-bold">
                      ${totalCost.toLocaleString()}
                    </td>
                    <td className="py-3 text-right text-green-400 font-mono font-bold">
                      ${totalSales.toLocaleString()}
                    </td>
                    <td className={`py-3 text-right font-mono font-bold ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${totalProfitLoss.toLocaleString()}
                    </td>
                    <td className={`py-3 text-right font-mono font-bold ${avgMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {totalSales > 0 ? `${avgMargin.toFixed(1)}%` : '-'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
